import React, {useCallback, useEffect, useRef} from 'react';
import {useProperty} from '@olympeio/core';
import {GeneralDetails} from './general_details/GeneralDetails.jsx';
import {HeaderDetails} from './general_details/header_details/HeaderDetails.jsx';
import {SendingOptionsDetails} from './po_sending_options/SendingOptionsDetails.jsx';
import {AccountDetails} from './account_details/AccountDetails.jsx';
import {TotalBudget} from './total_budget/TotalBudget.jsx';
import {TabSelector} from './utils/TabSelector.jsx';
import {AttachmentsDetails} from './attachments_comments/AttachmentsDetails.jsx';
import {InternalComments} from './attachments_comments/InternalComments.jsx';
import {LineItems} from './item_details/LineItems.jsx';
import PropTypes from 'prop-types';
import {ActionButtons} from './action_buttons/ActionButtons.jsx';
import {PurchaseRequest} from '../../models/PurchaseRequest';
import {requestStores} from '../ListOfPREditors.brick.jsx';
import {verifySAPApiPermission} from '../../utils/verifySAPApiPermission';
import UserController from '../../controllers/UserController';
import {PurchaseRequestController} from '../../controllers/PurchaseRequestController';
import {LAMBDA_NAMES} from '../../utils/constants';
import {BrickContext} from 'olympe';

/**
 * @param {ExpandedPREditor.propTypes} props
 * @return {JSX.Element}
 * @constructor
 */
export function ExpandedPREditor(props) {
    const useRequestStore = requestStores.get(props.prTag);
    const getNewPaymentTerms = props.context.get(LAMBDA_NAMES.GET_NEW_PAYMENT_TERMS);
    const currentTab = useProperty(props.context, 'Current tab', false);
    const submittedRequestsTab = currentTab === PurchaseRequest.tab.submitted;
    const spender = useRequestStore((state) => state.spender);
    const requester = useRequestStore((state) => state.requester);
    const setPrLocked = useRequestStore((state) => state.setPrLocked);
    const setSystemAlias = useRequestStore((state) => state.setSystemAlias);
    const setUserIdToCallSAPAPIs = useRequestStore((state) => state.setUserIdToCallSAPAPIs);
    const companyCode = useRequestStore((state) => state.companyCode);

    // These references are used by the tab selector.
    const headerRef = useRef(null);
    const generalDetailsRef = useRef();
    const itemDetailsRef = useRef();
    const accountDetailsRef = useRef();
    const poSendingOptionsRef = useRef();
    const attachmentsAndCommentsRef = useRef();

    // Change the system alias if the user change the company code
    useEffect(() => {
        PurchaseRequestController.fetchSystemAlias(props.context, companyCode.value).then((sysAliObj) => {
            setSystemAlias(sysAliObj.systemAlias);
        });
    }, [companyCode]);


    // This useEffect check if the current user is the first one checking the PR, if not, it disables all the fields
    useEffect(() => {
        const currentUserId = UserController.getCurrentUserName('EZPR-USER');
        setPrLocked(props.usersCheckingThePr.length > 1 && props.usersCheckingThePr[0].userId?.toUpperCase() !== currentUserId.toUpperCase());
    }, [props.usersCheckingThePr]);

    // This is being used for giving className to menu elements when scrolling down to specific section.
    useEffect(() => {
        const prEditor = document.querySelector('.list-of-pr');
        const sectionAll = document.querySelectorAll(`#pr-${props.prTag} > section[id]`);

        // Early return if elements don't exist
        if (!prEditor || sectionAll.length === 0) {
            return;
        }

        // This function calculates the offset of the sections to the top of PR list and sets the visibleSection accordingly.
        const scrollHandler = () => {
            const scrollY = prEditor.scrollTop;
            sectionAll.forEach((current) => {
                const sectionHeight = current.offsetHeight;
                const sectionTop = current.offsetTop;
                const sectionId = current.getAttribute('id');
                // 80 is the spacing for fixed header and section header margins.
                // 40 is for the spaces between sections.
                if (scrollY >= sectionTop - 80 && scrollY < sectionTop + sectionHeight - 40) {
                    const link = document.querySelector(`.${sectionId}-link-${props.prTag}`);
                    link?.classList.add('tab-selector-selected-tab');
                } else if (sectionId === 'generalDetails' && scrollY <= sectionTop) {
                    // Here, we handle general details link if user scrolls up higher than PR.
                    document?.querySelector(`.generalDetails-link-${props.prTag}`)?.classList.add('tab-selector-selected-tab');
                } else {
                    document?.querySelector(`.${sectionId}-link-${props.prTag}`)?.classList.remove('tab-selector-selected-tab');
                }
            });
        };

        const throttleScroll = () => {
            let timeoutId = null;
            return () => {
                if (timeoutId) return;
                scrollHandler();
                timeoutId = setTimeout(() => {
                    timeoutId = null;
                }, 250);
            };
        };

        // Store the throttled function to properly remove it later
        const throttledScrollHandler = throttleScroll();

        // Delay the initial scroll to ensure all content is rendered
        const initialScrollTimeout = setTimeout(() => {
            const generalDetailsElement = document?.getElementById('generalDetails');
            if (generalDetailsElement) {
                // Use 'nearest' instead of 'start' to prevent aggressive scrolling
                generalDetailsElement.scrollIntoView({behavior: 'smooth', block: 'nearest'});
            }
        }, 100);

        prEditor.addEventListener('scroll', throttledScrollHandler, {passive: true});

        // Remove scroll event handler on unmount.
        return () => {
            clearTimeout(initialScrollTimeout);
            prEditor.removeEventListener('scroll', throttledScrollHandler);
        };
    }, []);

    // This is being used for checking if spender or requester has permission for calling the SAP API.
    useEffect(() => {
        verifySAPApiPermission(props.context, spender, requester, setUserIdToCallSAPAPIs, getNewPaymentTerms);
    }, [spender, requester]);

    const scrollTo = useCallback((ele) => {
        // Use 'nearest' to avoid aggressive scrolling that can cause white space
        ele?.scrollIntoView({behavior: 'smooth', block: 'nearest'});
    }, []);

    return (
        <div className={'pr-editor-background'} id={`pr-${props.prTag}`}>
            <TabSelector
                context={props.context}
                prTag={props.prTag}
                scrollTo={scrollTo}
                handleClickOnExpand={props.handleClickOnExpand}
                handleClickOnDeny={props.handleClickOnDeny}
                handleClickOnApprove={props.handleClickOnApprove}
                handleClickOnSave={props.handleClickOnSave}
                handleClickOnSubmit={props.handleClickOnSubmit}
                handleClickOnCopy={props.handleClickOnCopy}
                handleClickOnSubmitToRequester={props.handleClickOnSubmitToRequester}
                handleClickOnWithdrawn={props.handleClickOnWithdrawn}
                handleClickOnReject={props.handleClickOnReject}
                headerRef={headerRef}
                generalDetailsRef={generalDetailsRef}
                itemDetailsRef={itemDetailsRef}
                accountDetailsRef={accountDetailsRef}
                poSendingOptionsRef={poSendingOptionsRef}
                attachmentsAndCommentsRef={attachmentsAndCommentsRef}
                loading={props.loading}
                disabledButtons={props.disabledButtons}
                currentTab={currentTab}
                usersCheckingThePr={props.usersCheckingThePr}
            />

            <section className={'pr-editor-scroll-target'} ref={generalDetailsRef} id={'generalDetails'}>
                <GeneralDetails
                    key={'requiredInfo' + props.prTag}
                    prTag={props.prTag}
                    context={props.context}
                    usersCheckingThePr={props.usersCheckingThePr}
                />
                <HeaderDetails
                    key={'generalInfo' + props.prTag}
                    prTag={props.prTag}
                    context={props.context}
                    parentLoading={props.loading}
                />
            </section>

            <section
                id={'itemDetails'}
                className={'pr-editor-centered-submodule pr-editor-scroll-target'}
                key={'materialInfo' + props.prTag}
                ref={itemDetailsRef}
            >
                <LineItems
                    prTag={props.prTag}
                    context={props.context}
                    setIsItemUngrouped={props.setIsItemUngrouped}
                />
            </section>

            <section className={'pr-editor-scroll-target'} ref={accountDetailsRef} id={'accountDetails'}>
                <AccountDetails
                    key={'accountDetails' + props.prTag}
                    prTag={props.prTag}
                    context={props.context}
                    submittedRequestsTab={submittedRequestsTab}
                />
            </section>

            <section className={'pr-editor-scroll-target'} ref={poSendingOptionsRef} id={'poSendingOptions'}>
                <SendingOptionsDetails
                    key={'sendingOptions' + props.prTag}
                    prTag={props.prTag}
                    context={props.context}
                />
            </section>

            <section className={'pr-editor-scroll-target-attachments'} ref={attachmentsAndCommentsRef} id={'attachmentsAndComments'}>
                <AttachmentsDetails
                    key={'attachments' + props.prTag}
                    prTag={props.prTag}
                    context={props.context}
                    currentTab={currentTab}
                />
                <InternalComments
                    key={'internalComments' + props.prTag}
                    prTag={props.prTag}
                    context={props.context}
                />

                <TotalBudget
                    key={'totalBudget' + props.prTag}
                    prTag={props.prTag}
                    context={props.context}
                />

                <ActionButtons
                    key={'saveSubmitButtons' + props.prTag}
                    prTag={props.prTag}
                    context={props.context}
                    handleClickOnDeny={props.handleClickOnDeny}
                    handleClickOnApprove={props.handleClickOnApprove}
                    handleClickOnSave={props.handleClickOnSave}
                    handleClickOnSubmit={props.handleClickOnSubmit}
                    handleClickOnSubmitToRequester={props.handleClickOnSubmitToRequester}
                    handleClickOnWithdrawn={props.handleClickOnWithdrawn}
                    handleClickOnReject={props.handleClickOnReject}
                    loading={props.loading}
                    submittedRequestsTab={submittedRequestsTab}
                    disabledButtons={props.disabledButtons}
                    setDisabledButtons={props.setDisabledButtons}
                />
            </section>
        </div>
    );
}

ExpandedPREditor.propTypes = {
    prTag: PropTypes.string,
    context: PropTypes.instanceOf(BrickContext).isRequired,
    handleClickOnExpand: PropTypes.func,
    handleClickOnDeny: PropTypes.func.isRequired,
    handleClickOnApprove: PropTypes.func.isRequired,
    handleClickOnSave: PropTypes.func.isRequired,
    handleClickOnSubmit: PropTypes.func.isRequired,
    handleClickOnCopy: PropTypes.func.isRequired,
    handleClickOnSubmitToRequester: PropTypes.func.isRequired,
    handleClickOnWithdrawn: PropTypes.func.isRequired,
    handleClickOnReject: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired,
    disabledButtons: PropTypes.object,
    setDisabledButtons: PropTypes.func,
    usersCheckingThePr: PropTypes.array,
    setIsItemUngrouped: PropTypes.func,
};
